/* =====================================================================
 * @auw/i18n — job role / interest option registries.
 *
 * Drives the onboarding wizard + settings "job role / interests" cards.
 * Labels for the preset interests are i18n-driven; job-role group
 * labels are still hard-coded Korean because the entire feature is
 * B2C-only and localising these five dozen strings has not bubbled up
 * yet. If Samsung / Sungwoo ever enable this surface we will revisit
 * and hoist them to the resource bundle.
 * ===================================================================== */

import i18n from './bootstrap';

export interface JobRoleGroup {
  category: string;
  roles: { value: string; label: string }[];
}

export const JOB_ROLE_GROUPS: JobRoleGroup[] = [
  {
    category: '기획/PM',
    roles: [
      { value: 'service_planner', label: '서비스 기획' },
      { value: 'pm', label: 'PM (Product Manager)' },
      { value: 'po', label: 'PO (Product Owner)' },
    ],
  },
  {
    category: '개발/IT',
    roles: [
      { value: 'frontend', label: '프론트엔드' },
      { value: 'backend', label: '백엔드' },
      { value: 'mobile', label: '모바일 앱' },
      { value: 'infra', label: '인프라' },
      { value: 'qa', label: 'QA' },
    ],
  },
  {
    category: '디자인',
    roles: [
      { value: 'ui_ux', label: 'UI/UX' },
      { value: 'graphic', label: '그래픽' },
      { value: 'video_3d', label: '영상/3D' },
    ],
  },
  {
    category: '마케팅/PR',
    roles: [
      { value: 'performance_marketing', label: '퍼포먼스 마케팅' },
      { value: 'content_marketing', label: '콘텐츠 마케팅' },
      { value: 'pr', label: '홍보/PR' },
    ],
  },
  {
    category: '데이터/AI',
    roles: [
      { value: 'data_analyst', label: '데이터 분석' },
      { value: 'data_engineer', label: '데이터 엔지니어' },
      { value: 'ai_ml', label: 'AI/ML 엔지니어' },
    ],
  },
  {
    category: '영업/비즈니스',
    roles: [
      { value: 'b2b_sales', label: 'B2B 영업' },
      { value: 'partnership', label: '제휴' },
      { value: 'biz_planning', label: '사업 기획' },
    ],
  },
  {
    category: '경영/지원',
    roles: [
      { value: 'hr', label: 'HR(인사)' },
      { value: 'finance', label: '재무/회계' },
      { value: 'general_affairs', label: '총무' },
      { value: 'management_support', label: '경영지원' },
    ],
  },
  {
    category: '기타',
    roles: [
      { value: 'student', label: '학생/취업준비생' },
      { value: 'freelancer', label: '프리랜서' },
      { value: 'custom', label: '직접 입력' },
    ],
  },
];

export const ALL_JOB_ROLES = JOB_ROLE_GROUPS.flatMap((g) => g.roles);

export function getJobRoleLabel(value: string): string {
  const found = ALL_JOB_ROLES.find((r) => r.value === value);
  return found?.label ?? value;
}

export function getJobRoleCategoryLabel(value: string): string {
  const group = JOB_ROLE_GROUPS.find((g) => g.roles.some((r) => r.value === value));
  return group?.category ?? '';
}

export interface InterestOption {
  value: string;
  label: string;
  emoji: string;
}

export const INTEREST_OPTIONS: InterestOption[] = [
  { value: 'tech', label: 'IT / 테크', emoji: '💻' },
  { value: 'finance', label: '경제 / 투자', emoji: '📈' },
  { value: 'health', label: '건강 / 운동', emoji: '🏋️' },
  { value: 'travel', label: '여행 / 아웃도어', emoji: '✈️' },
  { value: 'culture', label: '문화 / 예술', emoji: '🎨' },
  { value: 'food', label: '요리 / 미식', emoji: '🍽️' },
  { value: 'fashion', label: '패션 / 뷰티', emoji: '👗' },
  { value: 'pet', label: '반려동물', emoji: '🐾' },
];

const PRESET_INTEREST_TRANSLATION_KEYS: Record<string, string> = {
  tech: 'common:interests.tech',
  finance: 'common:interests.finance',
  health: 'common:interests.health',
  travel: 'common:interests.travel',
  culture: 'common:interests.culture',
  food: 'common:interests.food',
  fashion: 'common:interests.fashion',
  pet: 'common:interests.pet',
};

export const MAX_INTERESTS = 3;
export const MIN_INTERESTS = 3;
export const MAX_INTEREST_OPTION_COUNT = 12;
export const MAX_CUSTOM_INTEREST_LENGTH = 20;

const PRESET_INTEREST_VALUE_SET = new Set(INTEREST_OPTIONS.map((option) => option.value));

export function sanitizeInterestKeyword(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

export function normalizeInterestKeyword(value: string): string {
  return sanitizeInterestKeyword(value).toLowerCase();
}

export function isPresetInterestValue(value: string): boolean {
  return PRESET_INTEREST_VALUE_SET.has(normalizeInterestKeyword(value));
}

export function mergeCustomInterestLabels(
  customInterests: string[],
  selectedInterests: string[],
): string[] {
  const out: string[] = [];
  const seen = new Set<string>();

  const append = (value: string) => {
    const label = sanitizeInterestKeyword(value);
    const normalized = normalizeInterestKeyword(label);
    if (!label || !normalized || isPresetInterestValue(normalized) || seen.has(normalized)) {
      return;
    }
    seen.add(normalized);
    out.push(label);
  };

  customInterests.forEach(append);
  selectedInterests.forEach(append);

  return out;
}

export function getInterestDisplayLabel(value: string, customInterests: string[] = []): string {
  const normalized = normalizeInterestKeyword(value);
  const preset = INTEREST_OPTIONS.find((option) => option.value === normalized);
  if (preset) {
    return i18n.t(PRESET_INTEREST_TRANSLATION_KEYS[normalized] ?? '', {
      defaultValue: preset.label,
    });
  }

  const custom = customInterests.find(
    (interest) => normalizeInterestKeyword(interest) === normalized,
  );
  if (custom) return sanitizeInterestKeyword(custom);

  const fallback = sanitizeInterestKeyword(value).replace(/[_-]+/g, ' ');
  if (!fallback) {
    return i18n.t('common:interests.defaultLabel', { defaultValue: 'Interest' });
  }

  return fallback
    .split(' ')
    .map((token) => token.charAt(0).toUpperCase() + token.slice(1))
    .join(' ');
}
